import { IdeyaAgGridTable } from '../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { getBaseQuery, getBaseUrl } from '../../../apis/api.util.ts'
import {
  ColGroupDef,
  GridReadyEvent,
  IGetRowsParams,
  SortModelItem,
  ValueFormatterParams,
} from 'ag-grid-enterprise'
import {
  GetStatisticsRequest,
  GetStatisticsResponse,
} from '../../../apis/statistics/statistics-api.def.ts'
import {
  selectManagementInput,
  setCategory,
} from '../../../redux/slices/management-input.slice.ts'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { BaseQueryApi } from '@reduxjs/toolkit/query'
import {
  ColDef,
  ColumnResizedEvent,
  ColumnState,
  GridApi,
} from 'ag-grid-community'
import {
  coloredNumberCellStyle,
  currencyValueFormatter,
  millionFormatter,
  numberCellStyle,
  percentageValueFormatter,
} from '../../../utils/formatters.ts'
import { CustomCellRendererProps } from 'ag-grid-react'
import { StatisticsActionColumn } from './statistics-action-column.tsx'
import { BookNameCellRenderer } from '../../components/book-name-cell-renderer.tsx'
import { PeriodDependentHeaderComponent } from './statistics-period-dependent-header.tsx'
import './statistics-table.css'
import { useTranslation } from 'react-i18next'
import { STATISTICS_LIST_COLUMN_STATE_KEY } from '../../../utils/constants.ts'
import { IsTrialCellRenderer } from '../../components/is-trial-cell-renderer.tsx'
import { selectUser } from '../../../features/user/user.slice.ts'
import { useAppSelector } from '../../../redux/hooks.ts'
import { sortMappingStatistics } from './statistics-table.def.ts'

function buildStatisticListUrl(request: GetStatisticsRequest) {
  let region = request.region ?? ''
  let netCategory = request.netCategory ?? ''

  // Parse netCategory if it contains " - " separator
  if (request.netCategory && request.netCategory.includes(' - ')) {
    const parts = request.netCategory.split(' - ')
    region = parts[0] // "Greater China"
    netCategory = parts[1] // "Low Net"
  }

  const params = new URLSearchParams({
    page: String(request.page),
    pageSize: String(request.pageSize ?? ''),
    startDate: request.startDate ?? '',
    endDate: request.endDate ?? '',
    region: region,
    netCategory: netCategory,
    firmId: request.firmId ?? '',
    isTrial: request.isTrial ?? '',
    orderBy: request.orderBy ?? '',
    orderDirection: request.orderDirection ?? '',
  })
  return `/?${params.toString()}`
}

interface StatisticsTableProps {
  height: number
}

export const StatisticsTable: React.FC<StatisticsTableProps> = ({ height }) => {
  const { t } = useTranslation()
  const gridApiRef = useRef<GridApi | null>(null)
  const managementInput = useSelector(selectManagementInput)
  const userInfo = useAppSelector(selectUser)
  const dispatch = useDispatch()
  const [columnStateReset, setColumnStateReset] = useState<boolean>(false)

  // When navigating from Ticker Management to Statistics,
  // if the category is "Greater China", reset it to empty string
  useEffect(() => {
    if (managementInput.category === 'Greater China') {
      dispatch(setCategory(''))
    }
  }, [managementInput])

  const applyLocalStorageColumnState = () => {
    const savedState = localStorage.getItem(STATISTICS_LIST_COLUMN_STATE_KEY)
    if (savedState) {
      const state = JSON.parse(savedState)

      gridApiRef.current?.applyColumnState({
        state: state,
        applyOrder: true,
      })
    }
  }

  const columnDefs: ColDef[] = useMemo(() => {
    const baseColumns: ColGroupDef[] = [
      {
        headerName: t('statistics.statistics_table.columns.static_information'),
        children: [
          {
            headerName: '',
            field: 'bookId',
            pinned: 'left',
            maxWidth: 55,
            cellRenderer: (props: CustomCellRendererProps) => {
              if (props.value !== undefined && props.data.status === 'Active') {
                return (
                  <StatisticsActionColumn
                    data={{
                      bookId: props.data.bookId,
                      userName:
                        props.data.userName || props.data.bookName || '',
                      firmName: props.data.firmName || '',
                    }}
                  />
                )
              }
              return <></>
            },
          },
          {
            headerName: t('statistics.statistics_table.columns.book_name'),
            field: 'bookName',
            pinned: 'left',
            cellRenderer: BookNameCellRenderer,
          },
          {
            headerName: t('statistics.statistics_table.columns.firm_name'),
            field: 'firmName',
          },
          {
            headerName: t('statistics.statistics_table.columns.gmv'),
            field: 'gmv',
            type: 'numberColumn',
            valueFormatter: (params: ValueFormatterParams) =>
              millionFormatter(params, { decimal: 2 }),
          },
          {
            headerName: t('statistics.statistics_table.columns.net_on_gmv_pct'),
            field: 'netGMVRate',
            type: 'numberColumn',
            cellStyle: numberCellStyle,
            valueFormatter: percentageValueFormatter,
          },
          {
            headerName: t('statistics.statistics_table.columns.avg_duration'),
            field: 'avgDur',
            type: 'numberColumn',
          },
          {
            headerName: t('statistics.statistics_table.columns.bp'),
            field: 'bp',
            valueFormatter: (params: ValueFormatterParams) =>
              millionFormatter(params, { decimal: 2 }),
            type: 'numberColumn',
          },
          {
            headerName: t('statistics.statistics_table.columns.gmv_pct_bp'),
            field: 'gmvPctBp',
            valueFormatter: percentageValueFormatter,
            type: 'numberColumn',
          },
          {
            headerName: t('statistics.statistics_table.columns.daily_pnl'),
            field: 'dailyPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('statistics.statistics_table.columns.wtd_pnl'),
            field: 'wtdPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('statistics.statistics_table.columns.mtd_pnl'),
            field: 'mtdPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('statistics.statistics_table.columns.qtd_pnl'),
            field: 'qtdPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('statistics.statistics_table.columns.ytd_pnl'),
            field: 'ytdPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('statistics.statistics_table.columns.account_type'),
            field: 'isTrial',
            suppressHeaderMenuButton: true,
            cellRenderer: IsTrialCellRenderer,
            sortable: false,
          },
        ],
      },
      {
        headerName: t(
          'statistics.statistics_table.columns.period_dependent_data'
        ),
        headerGroupComponentParams: {
          innerHeaderGroupComponent: PeriodDependentHeaderComponent,
          innerHeaderGroupComponentParams: {
            startDate: managementInput.dateRange[0],
            endDate: managementInput.dateRange[1],
          },
        },
        headerClass: 'period-dependent-header',
        children: [
          {
            headerName: t('statistics.statistics_table.columns.avg_bp_rate'),
            headerClass: 'period-dependent-child-header',
            field: 'avgBPRate',
            type: 'numberColumn',
            cellStyle: numberCellStyle,
            valueFormatter: percentageValueFormatter,
            suppressMovable: true,
          },
          {
            headerName: t('statistics.statistics_table.columns.hit_rate'),
            headerClass: 'period-dependent-child-header',
            field: 'hitRate',
            type: 'numberColumn',
            cellStyle: numberCellStyle,
            valueFormatter: percentageValueFormatter,
            suppressMovable: true,
          },
          {
            headerName: t(
              'statistics.statistics_table.columns.abs_return_rate'
            ),
            headerClass: 'period-dependent-child-header',
            field: 'absReturnRate',
            type: 'numberColumn',
            cellStyle: numberCellStyle,
            valueFormatter: percentageValueFormatter,
            suppressMovable: true,
          },
          {
            headerName: t('statistics.statistics_table.columns.total_pnl'),
            headerClass: 'period-dependent-child-header',
            field: 'adjustedPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
            suppressMovable: true,
          },
        ],
      },
    ]

    // Only show account type for internal admin users
    if (userInfo.role === '') {
      baseColumns[0].children = baseColumns[0].children.filter(
        (col) => 'field' in col && col.field !== 'isTrial'
      )
    }

    // Skip localStorage order if reset was triggered
    if (columnStateReset) {
      setColumnStateReset(false)
      return baseColumns
    }

    const savedState = localStorage.getItem(STATISTICS_LIST_COLUMN_STATE_KEY)
    if (savedState) {
      const state = JSON.parse(savedState)

      // Flatten children for ordering
      const allChildren = [
        ...baseColumns[0].children,
        ...baseColumns[1].children,
      ] as ColDef[]

      const orderedChildren: ColDef[] = []
      const usedFields = new Set<string>()

      state.forEach((colState: ColumnState) => {
        const col = allChildren.find((c) => c.field === colState.colId)
        if (col) {
          const colCopy = { ...col }
          if (colState.width !== undefined) colCopy.width = colState.width
          orderedChildren.push(colCopy)
          usedFields.add(colCopy.field as string)
        }
      })
      // Add any missing columns
      allChildren.forEach((col) => {
        if (!usedFields.has(col.field as string)) {
          orderedChildren.push(col)
        }
      })

      // Rebuild baseColumns with ordered children
      const staticCount = baseColumns[0].children.length
      baseColumns[0].children = orderedChildren.slice(0, staticCount)
      baseColumns[1].children = orderedChildren.slice(staticCount)

      gridApiRef.current?.applyColumnState({
        state: state,
        applyOrder: true,
      })
      return baseColumns
    }

    return baseColumns
  }, [userInfo, managementInput.dateRange, t, columnStateReset])

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      initialWidth: 200,
      sortable: true,
      resizable: true,
      filter: false,
      mainMenuItems: (params) => {
        return params.defaultItems.map((item) => {
          if (item === 'resetColumns') {
            return {
              name: 'Reset Columns',
              action: () => {
                setColumnStateReset(true)
                localStorage.removeItem(STATISTICS_LIST_COLUMN_STATE_KEY)
                gridApiRef.current?.resetColumnState()
              },
            }
          }
          return item
        })
      },
    }
  }, [])

  const pageSize = 20
  const baseQuery = getBaseQuery(getBaseUrl('statistics'))

  const convertSortModel = (sortModel: SortModelItem[]) => {
    if (sortModel.length > 0) {
      const orderBy = sortMappingStatistics[sortModel[0].colId]
      const order = sortModel[0].sort.toUpperCase()

      return [orderBy, order]
    }
    return null
  }

  const datasource = useMemo(
    () => ({
      getRows: async (params: IGetRowsParams) => {
        const isFirstPage = params.startRow === 0
        if (isFirstPage) {
          gridApiRef.current?.showLoadingOverlay()
        }

        try {
          const sortResult = convertSortModel(params.sortModel)
          const orderBy = sortResult ? sortResult[0] : ''
          const order = sortResult ? sortResult[1] : ''

          const request: GetStatisticsRequest = {
            page: Math.floor(params.startRow / pageSize) + 1,
            pageSize: pageSize,
            startDate: dayjs(managementInput.dateRange[0]).format('YYYY-MM-DD'),
            endDate: dayjs(managementInput.dateRange[1]).format('YYYY-MM-DD'),
            netCategory: managementInput.category,
            firmId: managementInput.firm,
            isTrial: managementInput.accountStatisticsTrialType,
            orderBy: orderBy,
            orderDirection: order,
          }

          const result = await baseQuery(
            {
              url: buildStatisticListUrl(request),
              method: 'GET',
            },
            {} as BaseQueryApi,
            {}
          )

          const dataResult = result as { data?: GetStatisticsResponse }

          if (dataResult && dataResult.data && dataResult.data.data) {
            const statisticItems = dataResult.data.data.statisticsItems
            const lastRow =
              statisticItems.length < pageSize
                ? params.startRow + statisticItems.length
                : undefined
            if (statisticItems.length === 0 && isFirstPage) {
              gridApiRef.current?.showNoRowsOverlay()
            } else {
              gridApiRef.current?.hideOverlay()
            }
            params.successCallback(statisticItems, lastRow)
          } else {
            params.failCallback()
            if (isFirstPage) {
              gridApiRef.current?.hideOverlay()
            }
          }
        } catch {
          params.failCallback()
          if (isFirstPage) {
            gridApiRef.current?.hideOverlay()
          }
        }
      },
    }),
    [
      managementInput.dateRange,
      managementInput.firm,
      managementInput.category,
      pageSize,
      baseQuery,
    ]
  )

  const handleGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api
    const savedState = localStorage.getItem(STATISTICS_LIST_COLUMN_STATE_KEY)
    if (savedState) {
      const state = JSON.parse(savedState)
      gridApiRef.current.applyColumnState({
        state: state,
        applyOrder: true,
      })
    }
  }, [])

  const handleColumnChanged = useCallback(() => {
    if (!gridApiRef.current) return
    const state = gridApiRef.current.getColumnState()
    localStorage.setItem(
      STATISTICS_LIST_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }, [])

  const handleSortChanged = useCallback(() => {
    if (!gridApiRef.current) return
    const state = gridApiRef.current.getColumnState()
    localStorage.setItem(
      STATISTICS_LIST_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }, [])

  const handleColumnResized = useCallback((event: ColumnResizedEvent) => {
    if (
      !event.finished ||
      !gridApiRef.current ||
      event.source !== 'uiColumnResized'
    )
      return

    const state = gridApiRef.current.getColumnState()
    localStorage.setItem(
      STATISTICS_LIST_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }, [])

  return (
    <IdeyaAgGridTable
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      datasource={datasource}
      rowModelType={'infinite'}
      cacheBlockSize={pageSize}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onColumnResized={handleColumnResized}
      onSortChanged={handleSortChanged}
      onFirstDataRendered={applyLocalStorageColumnState}
      height={height}
    />
  )
}
